import { useState } from 'react';
import {
  BatteryFull,
  BatteryLow,
  BatteryMedium,
  BatteryWarning,
  Check,
  Copy,
  FlaskConical,
  Radio,
  RadioTower,
  WifiOff,
} from 'lucide-react';
import { computePositionFreshness, formatPositionAge, positionAgeMs } from '@mytag/shared';
import type { DeviceStatus, LocationType } from '@mytag/shared';
import { Badge, Button } from '@/components/ui/primitives';
import { LOCATION_TYPE_LABELS, STATUS_LABELS } from '@/lib/format';
import { cn } from '@/lib/utils';

export function StatusBadge({ status }: { status: DeviceStatus }): JSX.Element {
  if (status === 'online') {
    return (
      <Badge tone="success">
        <Radio className="h-3 w-3" aria-hidden />
        {STATUS_LABELS.online}
      </Badge>
    );
  }
  if (status === 'offline') {
    return (
      <Badge tone="danger">
        <WifiOff className="h-3 w-3" aria-hidden />
        {STATUS_LABELS.offline}
      </Badge>
    );
  }
  return (
    <Badge tone="neutral">
      <RadioTower className="h-3 w-3" aria-hidden />
      {STATUS_LABELS.unknown}
    </Badge>
  );
}

export function LocationTypeBadge({ type }: { type: LocationType }): JSX.Element {
  const tone = type === 'gps' ? 'info' : type === 'unknown' ? 'neutral' : 'warning';
  return <Badge tone={tone}>{LOCATION_TYPE_LABELS[type] ?? type}</Badge>;
}

/** Fraîcheur affichée comme durée numérique (ex. « 8 min », « 3 h 12 min »), jamais un simple libellé qualitatif. */
export function PositionFreshnessBadge({ lastUpdate }: { lastUpdate: string | null | undefined }): JSX.Element {
  const freshness = computePositionFreshness(lastUpdate);
  const tone = freshness === 'recent' ? 'success' : freshness === 'stale' ? 'warning' : 'neutral';
  const label = freshness === 'unknown' ? 'Non communiquée' : formatPositionAge(positionAgeMs(lastUpdate));
  return <Badge tone={tone}>{label}</Badge>;
}

/** Niveau de batterie : icône + pourcentage, seuils 20 / 50 %. */
export function BatteryGauge({
  level,
  className,
  showBar = false,
}: {
  level: number | null;
  className?: string;
  showBar?: boolean;
}): JSX.Element {
  if (level === null) {
    return <span className={cn('text-sm text-muted-foreground', className)}>—</span>;
  }

  const tone =
    level <= 15 ? 'text-danger' : level <= 30 ? 'text-warning' : level <= 60 ? 'text-foreground' : 'text-success';
  const Icon = level <= 15 ? BatteryWarning : level <= 30 ? BatteryLow : level <= 60 ? BatteryMedium : BatteryFull;

  return (
    <span className={cn('inline-flex items-center gap-2', className)}>
      <Icon className={cn('h-4 w-4', tone)} aria-hidden />
      <span className="tabular text-sm font-medium">{Math.round(level)} %</span>
      {showBar ? (
        <span className="h-1.5 w-16 overflow-hidden rounded-full bg-border" aria-hidden>
          <span
            className={cn(
              'block h-full rounded-full',
              level <= 15 ? 'bg-danger' : level <= 30 ? 'bg-warning' : 'bg-success',
            )}
            style={{ width: `${Math.max(2, Math.min(100, level))}%` }}
          />
        </span>
      ) : null}
    </span>
  );
}

export function MockBadge({ className }: { className?: string }): JSX.Element {
  return (
    <Badge tone="warning" className={cn('font-semibold', className)} title="Données simulées, aucun appel réseau vers Vernal">
      <FlaskConical className="h-3 w-3" aria-hidden />
      Mode mock
    </Badge>
  );
}

/** Copie un diagnostic dans le presse-papiers avec retour visuel. */
export function CopyButton({
  value,
  label = 'Copier',
  className,
  size = 'sm',
}: {
  value: string;
  label?: string;
  className?: string;
  size?: 'sm' | 'md';
}): JSX.Element {
  const [copied, setCopied] = useState(false);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  }

  return (
    <Button
      type="button"
      variant="outline"
      size={size}
      className={className}
      onClick={() => void copy()}
      icon={copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
    >
      {copied ? 'Copié' : label}
    </Button>
  );
}

/** Paire libellé/valeur, brique de base des fiches device. */
export function DataRow({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
      <dt className="shrink-0 text-xs text-muted-foreground">{label}</dt>
      <dd className={cn('min-w-0 truncate text-right text-sm', mono && 'font-mono text-xs')}>{value}</dd>
    </div>
  );
}
